import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { jwtDecode } from 'jwt-decode';
import {
  Observable,
  catchError,
  map,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { environment } from '../../../environments/environment';
import { stripRolePrefix } from '../helpers/role.helpers';
import {
  JwtPayload,
  LoginRequest,
  LoginResponse,
  MeResponse,
  UserInfo,
} from '../types/auth.types';

/**
 * Auth state holder + login / refresh / logout calls against the auth-service
 * (proxied by the gateway). The access token lives in memory (signal) only;
 * the refresh token persists in localStorage so the session survives reloads.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  /** localStorage key for the refresh token. Per-app namespaced to avoid collisions. */
  private readonly REFRESH_KEY = 'nawam.owner.refreshToken';
  private readonly API = `${environment.gatewayUrl}/auth/api/auth`;

  // ── state ────────────────────────────────────────────────────────────────
  private readonly _accessToken = signal<string | null>(null);
  private readonly _user = signal<UserInfo | null>(null);
  /**
   * True while the initial silent refresh + /me are in flight. Used by
   * `AppComponent` to keep the splash visible through the auth boot phase,
   * so the user doesn't see `/signin` flash before guards resolve.
   */
  private readonly _booting = signal(false);

  readonly accessToken = this._accessToken.asReadonly();
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => !!this._accessToken());
  readonly roles = computed(() => this._user()?.roles ?? []);
  readonly permissions = computed(() => this._user()?.permissions ?? []);
  readonly booting = this._booting.asReadonly();

  /**
   * Resolves once the initial silent refresh + /me have settled. Used by
   * route guards to avoid redirecting to /signin while the refresh is still
   * in flight (which would happen on every page reload).
   *
   * If no boot is in progress (no refresh token in localStorage, or boot
   * already done), resolves immediately.
   */
  whenReady(): Promise<void> {
    return this._bootPromise;
  }

  /** Resolved either immediately (no boot needed) or by the constructor's
   *  refresh+me subscription. */
  private _bootResolve!: () => void;
  private readonly _bootPromise = new Promise<void>((resolve) => {
    this._bootResolve = resolve;
  });

  constructor() {
    // Best-effort silent refresh on app boot if a refresh token is present,
    // then load the rich profile from /me.
    const stored = this.readRefreshToken();
    this.devLog('boot — refresh token in storage:', stored ? `${stored.slice(0, 12)}…` : '(none)');
    if (!stored) {
      // Nothing to do — guards can decide immediately.
      this._bootResolve();
      return;
    }
    this._booting.set(true);
    this.devLog('boot — calling POST', `${this.API}/refresh-token`);
    this.refresh()
      .pipe(
        switchMap(() =>
          this.me().pipe(catchError((err) => {
            this.devWarn('boot — /me failed (keeping JWT user)', err);
            return of(this._user());
          })),
        ),
      )
      .subscribe({
        next: () => {
          this.devLog('boot — OK, user is', this._user()?.username);
          this._booting.set(false);
          this._bootResolve();
        },
        error: (err) => {
          this.devError('boot — refresh FAILED, clearing session', err);
          this.clearSession();
          this._booting.set(false);
          this._bootResolve();
        },
      });
  }

  // ── dev-only logging (silent in prod build) ────────────────────────────
  // Tree-shaken in prod thanks to `environment.production` being a literal
  // `true` constant — the `if` body is dead-code-eliminated.
  private devLog(...args: unknown[]): void {
    if (!environment.production) console.log('[Auth]', ...args);
  }
  private devWarn(...args: unknown[]): void {
    if (!environment.production) console.warn('[Auth]', ...args);
  }
  private devError(...args: unknown[]): void {
    if (!environment.production) console.error('[Auth]', ...args);
  }

  // ── public API ───────────────────────────────────────────────────────────

  login(req: LoginRequest): Observable<UserInfo | null> {
    return this.http.post<LoginResponse>(`${this.API}/login`, req).pipe(
      tap((res) => this.setSession(res)),
      // Enrich with /me — if it fails, keep the JWT-derived user so the user
      // still ends up authenticated (guards may then send them to /forbidden).
      switchMap(() =>
        this.me().pipe(
          catchError((err: unknown) => {
            console.warn('GET /me failed after login, using JWT data', err);
            return of(this._user());
          }),
        ),
      ),
    );
  }

  /**
   * Exchange the stored refresh token for a fresh access token. Emits an
   * error if no refresh token is present locally. Does NOT call /me — that
   * is the caller's responsibility (constructor + login() do it explicitly).
   */
  refresh(): Observable<UserInfo | null> {
    const refreshToken = this.readRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }
    return this.http
      .post<LoginResponse>(`${this.API}/refresh-token`, { refreshToken })
      .pipe(
        tap((res) => this.setSession(res)),
        map(() => this._user()),
      );
  }

  /**
   * Fetch the rich user profile from `auth-service`. Updates the `user`
   * signal in place. Returns the merged UserInfo (or null if the response
   * is unusable).
   */
  me(): Observable<UserInfo | null> {
    return this.http.get<MeResponse>(`${this.API}/me`).pipe(
      tap((me) => this._user.set(this.mergeMe(me))),
      map(() => this._user()),
    );
  }

  logout(redirectToSignin = true): void {
    // Fire-and-forget server-side logout, then clear local state regardless.
    this.http
      .post(`${this.API}/logout`, {})
      .pipe(catchError(() => of(void 0)))
      .subscribe();
    this.clearSession();
    if (redirectToSignin) {
      void this.router.navigate(['/signin']);
    }
  }

  // ── authorization helpers ────────────────────────────────────────────────

  hasRole(role: string): boolean {
    return this.roles().includes(role);
  }

  hasAnyRole(...roles: string[]): boolean {
    if (!roles.length) return true;
    const have = this.roles();
    return roles.some((r) => have.includes(r));
  }

  hasAllRoles(...roles: string[]): boolean {
    const have = this.roles();
    return roles.every((r) => have.includes(r));
  }

  hasPermission(perm: string): boolean {
    return this.permissions().includes(perm);
  }

  hasAnyPermission(...perms: string[]): boolean {
    if (!perms.length) return true;
    const have = this.permissions();
    return perms.some((p) => have.includes(p));
  }

  hasAllPermissions(...perms: string[]): boolean {
    const have = this.permissions();
    return perms.every((p) => have.includes(p));
  }

  // ── internals ────────────────────────────────────────────────────────────

  private setSession(res: LoginResponse): void {
    this._accessToken.set(res.token);
    this._user.set(this.decode(res.token));
    if (res.refreshToken) {
      try {
        localStorage.setItem(this.REFRESH_KEY, res.refreshToken);
      } catch {
        /* storage disabled — session will not survive reload */
      }
    }
  }

  private clearSession(): void {
    this._accessToken.set(null);
    this._user.set(null);
    try {
      localStorage.removeItem(this.REFRESH_KEY);
    } catch {
      /* ignore */
    }
  }

  private readRefreshToken(): string | null {
    try {
      return localStorage.getItem(this.REFRESH_KEY);
    } catch {
      return null;
    }
  }

  /**
   * Merge a /me payload onto the current UserInfo. /me is authoritative for
   * roles + permissions + identity fields; the JWT-derived `exp` is kept
   * because /me does not return it.
   */
  private mergeMe(me: MeResponse): UserInfo {
    const previous = this._user();
    const roles = (me.roles ?? []).map(stripRolePrefix);
    const fullName = [me.firstName, me.lastName].filter(Boolean).join(' ');
    return {
      userId: String(me.id),
      username: me.username ?? previous?.username ?? null,
      displayName: fullName || me.username || previous?.displayName || null,
      email: me.email ?? previous?.email ?? null,
      roles,
      permissions: me.permissions ?? [],
      exp: previous?.exp ?? null,
      // /me-only enrichment
      id: me.id,
      firstName: me.firstName,
      lastName: me.lastName,
      sex: me.sex,
      phone: me.phone,
      address: me.address,
      avatarUrl: me.avatarUrl,
      active: me.active,
      changePassword: me.changePassword,
      notifSubId: me.notifSubId,
    };
  }

  private decode(token: string): UserInfo | null {
    try {
      const p = jwtDecode<JwtPayload>(token);
      const realmRoles = p.realm_access?.roles ?? [];
      const perms = (p.authorization?.permissions ?? [])
        .map((x) => x.rsname)
        .filter((x): x is string => !!x);
      const userId =
        p.nawam_user_id != null ? String(p.nawam_user_id) : (p.sub ?? null);
      const fullName = [p.given_name, p.family_name].filter(Boolean).join(' ');
      const displayName =
        p.name ?? (fullName || p.preferred_username || p.email || null);
      return {
        userId,
        username: p.preferred_username ?? p.email ?? null,
        displayName,
        email: p.email ?? null,
        roles: realmRoles,
        permissions: perms,
        exp: p.exp ?? null,
      };
    } catch {
      return null;
    }
  }
}

